/**
 * Static grid map. Row-major typed arrays for locality; cheap `tileAt` /
 * `isBlocking` lookups. No PixiJS. See CLAUDE.md architecture rule 4.
 */

import { CONFIG } from '../config';
import type { Camera } from './camera';
import type { Container } from './container';
import type { Lock } from './lock';
import { TileType, isBlocking as tileIsBlocking, defaultGlyph, tileDestructible } from './tiles';

/** Where a light fixture is mounted (cosmetic for now — all flood the same). */
export type LightMount = 'ceiling' | 'wall' | 'floor';

/** A light source placed in the map. An ARTIFICIAL fixture contributes to the
 *  light field only while its room's breaker is on; a NATURAL source (sunlight
 *  through a window) is off the electrical grid and always contributes. */
export interface LightSource {
  x: number;
  y: number;
  /** The room whose fusebox breaker switches this fixture. Ignored when
   *  `natural` is true (nothing electrical can switch daylight). */
  roomId: number;
  /** Light level at the source tile (0..CONFIG.light.max). */
  intensity: number;
  /** Reach in tiles; light falls off ~linearly to 0 at this distance. */
  radius: number;
  mount: LightMount;
  /** True for NATURAL light (sunlight admitted by a window). Never gated by a
   *  breaker — the electrical system cannot control it. Absent/false = an
   *  artificial fixture on the grid. */
  natural?: boolean;
}

/** A short-lived point light (e.g. a muzzle flash) that adds to the light field
 *  for its lifetime without a full field recompute. */
export interface DynamicLight {
  x: number;
  y: number;
  intensity: number;
  radius: number;
  /** Remaining life in seconds; removed at ≤ 0. */
  ttl: number;
  /** Original life, for fade math. */
  life: number;
}

/** A named rectangular room. Its `lightsOn` breaker is toggled at the fusebox. */
export interface Room {
  id: number;
  name: string;
  /** Inclusive tile bounds. */
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  lightsOn: boolean;
  /** Guards don't patrol this room (e.g. the spawn room, the janitor closet). */
  noPatrol?: boolean;
  /** Room-type slug from procedural generation (e.g. 'lobby', 'open-office',
   *  'server', 'restroom'). Drives prop templates + population. Absent on the
   *  hand-built tutorial map. */
  kind?: string;
}

export interface MapData {
  width: number;
  height: number;
  /** Building name (e.g. "Chagat Corp") + floor number — used to name
   *  location-tied items like keys ("Chagat Corp 1F Janitor Key"). */
  title: string;
  floor: number;
  /** Row-major: index = y * width + x */
  tiles: Uint8Array;
  /** Baked glyph per tile (for cubicle autotile etc.); index matches `tiles`. */
  glyphs: string[];
  /** Player spawn tile. */
  spawn: { x: number; y: number };
  /** Remaining HP of damaged destructible props, keyed by tile index. Absent =
   *  full HP (lazily populated on first hit; cleared on destruction). */
  propHp: Map<number, number>;
  /** Tile indices whose visuals changed this frame (e.g. a destroyed prop). The
   *  renderer rebuilds these and clears the set. */
  dirtyTiles: Set<number>;
  /** Named rooms (for lighting + fusebox breakers). */
  rooms: Room[];
  /** Light fixtures placed in the map. */
  lights: LightSource[];
  /** Per-tile light level 0..CONFIG.light.max (index matches `tiles`). Defaults
   *  to fully-lit so a map with no configured lighting behaves as before; call
   *  `computeLightField` after placing rooms + lights to populate it for real. */
  lightField: Float32Array;
  /** Bumped whenever the light field changes so the renderer re-tints. */
  lightVersion: number;
  /** Per-tile guard-flashlight illumination, recomputed every tick from guards'
   *  torches (index matches `tiles`). Layered into `lightLevelAt` (max-combined)
   *  so a beam lights the player for detection + brightens the tiles. */
  flashlightField: Float32Array;
  /** Transient point lights (muzzle flashes) layered over the static field. */
  dynamicLights: DynamicLight[];
  /** Fusebox tile (player interacts from an adjacent tile), or null. */
  fusebox: { x: number; y: number } | null;
  /** Wall-mounted security cameras watching this floor (sim/camera.ts). */
  cameras: Camera[];
  /** Security stations — the manned desks/terminal banks in a Security office.
   *  A guard assigned to the room stands at one instead of roaming (guard.ts
   *  `manStation`), which makes the nerve centre a fixed hazard rather than a
   *  wandering one. Placed by generation; empty on the hand-built test map. */
  stations: { x: number; y: number }[];
  /** Doors placed in the map — per-door kind + optional lock. The tile itself is
   *  `Door` (open) or `DoorClosed` (shut); this holds the metadata. */
  doors: DoorInfo[];
  /** Locks guarding doors + containers (sim/lock.ts). Focusable + shootable. */
  locks: Lock[];
  /** Lootable containers (sim/container.ts). */
  containers: Container[];
}

/** How a door is operated. `electronic` is a stub for the future cybersecurity
 *  minigame (access panels); it behaves as a normal door for now. */
export type DoorKind = 'normal' | 'electronic';

export interface DoorInfo {
  x: number;
  y: number;
  kind: DoorKind;
  /** Guarding lock id (→ `MapData.locks`), or null for an unlocked door. */
  lockId: number | null;
  /** The BUILDING's own lock: staff carry keys to it, so NPCs pass through
   *  normally and only the intruder is stopped. Generated door locks are all
   *  like this; a lock the fiction says nobody has a key to (the janitor's
   *  closet on the tutorial map) leaves it false and stops everyone. */
  staffKeyed?: boolean;
}

export function inBounds(map: MapData, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < map.width && y < map.height;
}

/**
 * A room's interior bounds — the AABB shrunk one tile inward to exclude the wall
 * outline. Collapses gracefully for thin rooms (never inverts): if shrinking
 * would cross over, the original span on that axis is kept.
 */
export function roomInteriorBounds(room: Room): { x0: number; y0: number; x1: number; y1: number } {
  const x0 = room.x1 - room.x0 >= 2 ? room.x0 + 1 : room.x0;
  const x1 = room.x1 - room.x0 >= 2 ? room.x1 - 1 : room.x1;
  const y0 = room.y1 - room.y0 >= 2 ? room.y0 + 1 : room.y0;
  const y1 = room.y1 - room.y0 >= 2 ? room.y1 - 1 : room.y1;
  return { x0, y0, x1, y1 };
}

export function tileAt(map: MapData, x: number, y: number): TileType {
  if (!inBounds(map, x, y)) return TileType.Void;
  return map.tiles[y * map.width + x] as TileType;
}

export function glyphAt(map: MapData, x: number, y: number): string {
  if (!inBounds(map, x, y)) return ' ';
  return map.glyphs[y * map.width + x] ?? defaultGlyph(tileAt(map, x, y));
}

/** Does the given tile block movement for an entity trying to enter it? */
export function isBlocked(map: MapData, x: number, y: number): boolean {
  return tileIsBlocking(tileAt(map, x, y));
}

/**
 * After all tile types are set, walk cubicle partitions and pick the right
 * box-drawing glyph based on which of the four axis neighbors are also
 * partitions.
 */
export function autotileCubicles(map: MapData): void {
  for (let y = 0; y < map.height; y++) {
    for (let x = 0; x < map.width; x++) {
      if (tileAt(map, x, y) !== TileType.CubiclePartition) continue;
      const n = tileAt(map, x, y - 1) === TileType.CubiclePartition;
      const s = tileAt(map, x, y + 1) === TileType.CubiclePartition;
      const e = tileAt(map, x + 1, y) === TileType.CubiclePartition;
      const w = tileAt(map, x - 1, y) === TileType.CubiclePartition;
      map.glyphs[y * map.width + x] = pickPartitionGlyph(n, s, e, w);
    }
  }
}

function pickPartitionGlyph(n: boolean, s: boolean, e: boolean, w: boolean): string {
  const h = e || w;
  const v = n || s;
  if (h && v) {
    // Corners and T/+ junctions.
    if (n && s && e && w) return '╬';
    if (n && s && e) return '╠';
    if (n && s && w) return '╣';
    if (e && w && n) return '╩';
    if (e && w && s) return '╦';
    if (n && e) return '╚';
    if (n && w) return '╝';
    if (s && e) return '╔';
    if (s && w) return '╗';
  }
  if (h) return '═';
  if (v) return '║';
  return '═'; // isolated single partition tile — arbitrary
}

/** Create a blank Void map, ready for a builder to paint tiles into. */
export function blankMap(width: number, height: number): MapData {
  const size = width * height;
  const tiles = new Uint8Array(size).fill(TileType.Void);
  const glyphs = new Array<string>(size).fill(' ');
  // Default fully lit: a map with no lighting configured behaves exactly as
  // before (full detection everywhere). `computeLightField` overwrites this.
  const lightField = new Float32Array(size).fill(CONFIG.light.max);
  return {
    width,
    height,
    title: '',
    floor: 1,
    tiles,
    glyphs,
    spawn: { x: 0, y: 0 },
    propHp: new Map(),
    dirtyTiles: new Set(),
    rooms: [],
    lights: [],
    lightField,
    lightVersion: 0,
    flashlightField: new Float32Array(size),
    dynamicLights: [],
    fusebox: null,
    cameras: [],
    stations: [],
    doors: [],
    locks: [],
    containers: [],
  };
}

const NEIGHBORS8: ReadonlyArray<readonly [number, number]> = [
  [-1, -1], [0, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [0, 1], [1, 1],
];

/**
 * Apply `amount` damage to a destructible prop at (x, y). Non-destructible
 * tiles are untouched. When HP runs out the tile becomes its `rubble` type
 * (floor), cubicle autotiling is refreshed, and the tile + its neighbours are
 * marked dirty so the renderer rebuilds them. Returns what happened.
 */
export function damageTile(
  map: MapData,
  x: number,
  y: number,
  amount: number,
): 'destroyed' | 'damaged' | 'none' {
  const d = tileDestructible(tileAt(map, x, y));
  if (!d) return 'none';
  const idx = y * map.width + x;
  const hp = (map.propHp.get(idx) ?? d.hp) - amount;
  if (hp <= 0) {
    map.propHp.delete(idx);
    setTile(map, x, y, d.rubble);
    autotileCubicles(map); // refresh neighbour partition corners
    map.dirtyTiles.add(idx);
    for (const [dx, dy] of NEIGHBORS8) {
      if (inBounds(map, x + dx, y + dy)) map.dirtyTiles.add((y + dy) * map.width + (x + dx));
    }
    return 'destroyed';
  }
  map.propHp.set(idx, hp);
  return 'damaged';
}

/** Name a location-tied item at (x, y) as "Building FloorF Room ItemType" —
 *  e.g. "Chagat Corp 1F Janitor Key". Used for keys and similar items. */
export function itemLocationLabel(map: MapData, x: number, y: number, itemType: string): string {
  const room = map.rooms.find((r) => x >= r.x0 && x <= r.x1 && y >= r.y0 && y <= r.y1);
  return [map.title, `${map.floor}F`, room?.name, itemType]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** The door registered at (x, y), or null. */
export function doorAt(map: MapData, x: number, y: number): DoorInfo | null {
  return map.doors.find((d) => d.x === x && d.y === y) ?? null;
}

/** True if (x,y) is a CLOSED, unlocked, normal door an NPC may walk through
 *  (it opens it as it passes — see the guard/janitor movement step). */
export function npcOpenableDoor(map: MapData, x: number, y: number): boolean {
  if (tileAt(map, x, y) !== TileType.DoorClosed) return false;
  const d = doorAt(map, x, y);
  if (d === null || d.kind !== 'normal') return false;
  // Staff have keys to their own building — a locked office door stops the
  // intruder, not the guard who works there.
  return !doorLocked(map, d) || d.staffKeyed === true;
}

/** Is this door currently locked (its lock still engaged)? */
export function doorLocked(map: MapData, door: DoorInfo): boolean {
  if (door.lockId === null) return false;
  const lk = map.locks.find((l) => l.id === door.lockId);
  return lk !== undefined && lk.locked;
}

/**
 * Toggle a door open/closed. Closing always works; opening is refused while the
 * door's lock is engaged. Marks the tile dirty. Because a closed door occludes
 * sight AND light, the CALLER must recompute the light field after an
 * 'opened'/'closed' result (main.ts does — avoids a map→light import cycle).
 */
/**
 * The glyph a door shows, from the wall it sits in AND whether it's open.
 *
 * Open and closed used to share `║`, so a shut door in a north-south wall was
 * indistinguishable from an open one in an east-west wall — you'd read a door as
 * already open and walk into it. Now no glyph means two things:
 *
 *   wall runs ↕ (door blocks east-west)   closed `║`   open `─`
 *   wall runs ↔ (door blocks north-south) closed `═`   open `│`
 *
 * The closed leaf lies ALONG its wall (it fills the gap); the open leaf lies
 * across it (swung out of the way). So "thick line continuing the wall" always
 * reads shut, and "thin line crossing it" always reads open.
 */
export function doorGlyph(map: MapData, x: number, y: number, open: boolean): string {
  // A door's wall runs whichever way its solid neighbours do.
  const solid = (dx: number, dy: number): boolean => {
    const t = tileAt(map, x + dx, y + dy);
    return t === TileType.Wall || t === TileType.CubiclePartition || t === TileType.Window;
  };
  const vertical = solid(0, -1) || solid(0, 1);   // wall above/below → runs ↕
  const horizontal = solid(-1, 0) || solid(1, 0); // wall left/right → runs ↔
  // A door in a ↕ wall blocks passage east-west.
  if (vertical && !horizontal) return open ? '─' : '║';
  if (horizontal && !vertical) return open ? '│' : '═';
  // Ambiguous (corner or free-standing): fall back to the vertical reading.
  return open ? '─' : '║';
}

/** Re-stamp a door tile's glyph from its wall + state. Call after any change. */
export function refreshDoorGlyph(map: MapData, x: number, y: number): void {
  const t = tileAt(map, x, y);
  if (t !== TileType.Door && t !== TileType.DoorClosed) return;
  const idx = y * map.width + x;
  map.glyphs[idx] = doorGlyph(map, x, y, t === TileType.Door);
  map.dirtyTiles.add(idx);
}

export function toggleDoor(map: MapData, x: number, y: number): 'opened' | 'closed' | 'locked' | 'none' {
  const door = doorAt(map, x, y);
  if (!door) return 'none';
  const t = tileAt(map, x, y);
  const idx = y * map.width + x;
  if (t === TileType.Door) {
    setTile(map, x, y, TileType.DoorClosed);
    refreshDoorGlyph(map, x, y); // the leaf swings back across the gap
    map.dirtyTiles.add(idx);
    return 'closed';
  }
  if (t === TileType.DoorClosed) {
    if (doorLocked(map, door)) return 'locked';
    setTile(map, x, y, TileType.Door);
    refreshDoorGlyph(map, x, y);
    map.dirtyTiles.add(idx);
    return 'opened';
  }
  return 'none';
}

/** Paint a single tile and its default glyph. */
export function setTile(map: MapData, x: number, y: number, t: TileType): void {
  if (!inBounds(map, x, y)) return;
  const i = y * map.width + x;
  map.tiles[i] = t;
  map.glyphs[i] = defaultGlyph(t);
}

/** Paint a filled rectangle (inclusive bounds). */
export function fillRect(
  map: MapData,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  t: TileType,
): void {
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) setTile(map, x, y, t);
  }
}

/** Paint the border of a rectangle (inclusive bounds). */
export function outlineRect(
  map: MapData,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  t: TileType,
): void {
  for (let x = x0; x <= x1; x++) {
    setTile(map, x, y0, t);
    setTile(map, x, y1, t);
  }
  for (let y = y0; y <= y1; y++) {
    setTile(map, x0, y, t);
    setTile(map, x1, y, t);
  }
}
